import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  postDescargarDocumentos,
  getValidaciones,
  patchEstado,
} from "../../services/reporteKokam/ValidacionesritService";
import "../../styles/AprobadosKokam/ValidadorImagenes.css";

import config from "../../config";
const API_BASE = `${config.baseUrl}/api/kokam`;

const ITEMS_PER_PAGE = 50;

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────

export default function ValidadorImagenes() {
  const token = useSelector((state) => state.auth.user?.token);

  const [input,       setInput]       = useState("");
  const [loading,     setLoading]     = useState(false);
  const [loadingGet,  setLoadingGet]  = useState(false);
  const [resumen,     setResumen]     = useState(null);
  const [tabla,       setTabla]       = useState([]);
  const [error,       setError]       = useState(null);
  const [selectAll,   setSelectAll]   = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const cedulas = input
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  // Última lista de cédulas escrita, disponible de inmediato para cargarTabla
  // sin necesidad de que cambie su identidad (useCallback) en cada tecleo.
  const cedulasRef = useRef(cedulas);
  cedulasRef.current = cedulas;

  // ── Calcular paginación ──
  const totalPages    = Math.ceil(tabla.length / ITEMS_PER_PAGE);
  const startIndex    = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTabla = tabla.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // ── Cargar tabla (filtrada por las cédulas que se están buscando en este
  // momento) ── Nunca borra nada de la BD: si no hay cédulas escritas,
  // simplemente no muestra nada, en vez de traer todo lo descargado alguna vez.
  const cargarTabla = useCallback(async () => {
    const cedulasActuales = cedulasRef.current;

    if (cedulasActuales.length === 0) {
      setTabla([]);
      setSelectAll(false);
      return;
    }

    setLoadingGet(true);
    try {
      const data = await getValidaciones(cedulasActuales, token);
      setTabla(data);
      setCurrentPage(1); // resetear a página 1 al recargar
      const todosRechazados = data.length > 0 && data.every((r) => r.estado === 0);
      setSelectAll(todosRechazados);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingGet(false);
    }
  }, [token]);

  useEffect(() => {
    cargarTabla();
  }, [cargarTabla]);

  // Sincronizar checkbox global cuando cambia la tabla
  useEffect(() => {
    if (tabla.length === 0) { setSelectAll(false); return; }
    setSelectAll(tabla.every((r) => r.estado === 0));
  }, [tabla]);

  // ── Optimistic update individual ──
  const handleEstado = async (identificacion, estabaRechazado) => {
    const nuevoEstado = estabaRechazado ? 1 : 0;

    setTabla((prev) =>
      prev.map((r) =>
        r.identificacion === identificacion ? { ...r, estado: nuevoEstado } : r
      )
    );

    try {
      await patchEstado(identificacion, nuevoEstado, token);
    } catch {
      setTabla((prev) =>
        prev.map((r) =>
          r.identificacion === identificacion
            ? { ...r, estado: estabaRechazado ? 0 : 1 }
            : r
        )
      );
    }
  };

  // ── Seleccionar / deseleccionar todos ──
  const handleSelectAll = async (e) => {
    const checked     = e.target.checked;
    const nuevoEstado = checked ? 0 : 1;

    setSelectAll(checked);
    setTabla((prev) => prev.map((r) => ({ ...r, estado: nuevoEstado })));

    try {
      await Promise.allSettled(
        tabla.map((r) => patchEstado(r.identificacion, nuevoEstado, token))
      );
    } catch (e) {
      console.error("Error actualizando todos:", e);
    }
  };

  // ── Submit descarga ──
  const handleSubmit = async () => {
    if (!cedulas.length) return;
    setLoading(true);
    setError(null);
    setResumen(null);

    try {
      const resp = await postDescargarDocumentos(cedulas, token);
      setResumen(resp.data);
      // No se limpia el campo: la tabla se filtra por lo que está escrito,
      // así que borrarlo dejaría el resultado visualmente inconsistente.
      await cargarTabla();
    } catch (e) {
      setError(e?.message ?? "Error desconocido");
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setInput("");
    setResumen(null);
    setError(null);
    // No borra nada de la BD: solo deja de mostrar en pantalla lo que ya no se está buscando.
    setTabla([]);
  };

  // ── Handlers de paginación ──
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Genera array de números de página a mostrar (máx 5 botones)
  const getPageNumbers = () => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="vi-page">

      {/* ── SIDEBAR ── */}
      <div className="vi-sidebar">
        <div className="vi-sidebar-header">
          <span className="vi-tag">VALIDACIÓN RIT</span>
          <h2 className="vi-sidebar-title">Descarga de Imágenes</h2>
          <p className="vi-sidebar-sub">
            Ingresa las cédulas separadas por comas para descargar y registrar
            sus imágenes documentales.
          </p>
          <div
            className="vi-alert-info"
            title="Este módulo solo trae documentos de la encuesta 509. "
          >
            <span>ℹ</span>
            <span>
              Este módulo consulta únicamente la <strong>encuesta 509</strong> (Caracterización / Vinculación).
            </span>
          </div>
        </div>

        <div className="vi-sidebar-body">
          <label className="vi-label">
            Números de cédula
            {cedulas.length > 0 && (
              <span className="vi-counter">{cedulas.length} detectadas</span>
            )}
          </label>

          <textarea
            className="vi-textarea"
            placeholder="Ej: 1234567890, 0987654321"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            rows={6}
          />

          {cedulas.length > 0 && (
            <div className="vi-chips">
              {cedulas.slice(0, 8).map((c, i) => (
                <span key={i} className="vi-chip">{c}</span>
              ))}
              {cedulas.length > 8 && (
                <span className="vi-chip vi-chip--more">
                  +{cedulas.length - 8} más
                </span>
              )}
            </div>
          )}

          <div className="vi-actions">
            <button
              className="vi-btn-secondary"
              onClick={handleLimpiar}
              disabled={loading}
            >
              Limpiar
            </button>
            <button
              className="vi-btn-primary"
              onClick={handleSubmit}
              disabled={loading || !cedulas.length}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Spinner /> Procesando...
                </span>
              ) : (
                "Iniciar descarga"
              )}
            </button>
          </div>

          {error && (
            <div className="vi-alert-error">
              <span>✘</span>
              <span>{error}</span>
            </div>
          )}

          {resumen && (
            <div className="vi-resumen">
              <p className="vi-resumen-title">✔ Proceso finalizado</p>
              <div className="vi-stats-grid">
                <Stat label="Total"       value={resumen.total}            color="#334155" />
                <Stat label="Completos"   value={resumen.completos}        color="#16a34a" />
                <Stat label="Incompletos" value={resumen.parciales}        color="#b45309" />
                <Stat label="Sin imagen"  value={resumen.sinNingunaImagen} color="#b91c1c" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── TABLA ── */}
      <div className="vi-main">
        <div className="vi-table-header">
          <div>
            <h3
              className="vi-table-title"
              title="Tabla validaciones_rit — datos exclusivos de la encuesta 509 (Caracterización / Vinculación)"
            >
              Registros en validaciones_rit (Encuesta 509)
            </h3>
            <p className="vi-table-sub">
              {loadingGet
                ? "Cargando..."
                : `${tabla.length} registros encontrados`}
            </p>
          </div>
          <button
            className="vi-btn-refresh"
            onClick={cargarTabla}
            disabled={loadingGet}
          >
            {loadingGet ? <Spinner dark /> : "↻ Actualizar"}
          </button>
        </div>

        <div className="vi-table-wrapper">
          {loadingGet && tabla.length === 0 ? (
            <div className="vi-empty-state">
              <Spinner dark />
              Cargando registros...
            </div>
          ) : tabla.length === 0 ? (
            <div className="vi-empty-state">
              <span className="vi-empty-icon">📂</span>
              <span>No hay validaciones registradas aún.</span>
            </div>
          ) : (
            <table className="vi-table">
              <thead>
                <tr>
                  <th className="vi-th-check">
                    <label className="vi-check-wrap" title="Marcar todos como rechazados">
                      <input
                        type="checkbox"
                        className="vi-checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <span className="vi-check-label">Rechazar</span>
                    </label>
                  </th>
                  <th>Cédula</th>
                  <th>Imágenes</th>
                  <th>Doc. Anverso</th>
                  <th>Doc. Frontal</th>
                  <th>Firma Titular</th>
                  <th>Firma Gestor</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTabla.map((row) => {
                  const rechazado = row.estado === 0;
                  return (
                    <tr
                      key={row.identificacion}
                      className={rechazado ? "vi-tr--rechazado" : ""}
                    >
                      <td className="vi-td-check">
                        <input
                          type="checkbox"
                          className="vi-checkbox"
                          checked={rechazado}
                          onChange={() => handleEstado(row.identificacion, rechazado)}
                          title={rechazado ? "Rechazado — click para aprobar" : "Aprobado — click para rechazar"}
                        />
                      </td>
                      <td className="vi-td-cedula">
                        {row.identificacion}
                      </td>
                      <td className="vi-td-center">
                        <ImagenBadge count={row.img_encontradas} />
                      </td>
                      <td className="vi-td-center">
                        <ImagenCell ruta={row.doc_anverso}    token={token} />
                      </td>
                      <td className="vi-td-center">
                        <ImagenCell ruta={row.doc_frontal}    token={token} />
                      </td>
                      <td className="vi-td-center">
                        <ImagenCell ruta={row.firma_personal} token={token} />
                      </td>
                      <td className="vi-td-center">
                        <ImagenCell ruta={row.firma_gestor} token={token} />
                      </td>
                      <td className="vi-td-fecha">
                        {rechazado && (
                          <span className="vi-badge-rechazado">Rechazado</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── PAGINADOR ── */}
        {totalPages > 1 && (
          <div className="vi-pagination">
            <span className="vi-pagination-info">
              Página {currentPage} de {totalPages}
              &nbsp;·&nbsp;
              {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, tabla.length)} de {tabla.length}
            </span>

            <div className="vi-pagination-controls">
              {/* Primera página */}
              <button
                className="vi-page-btn"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                title="Primera página"
              >
                «
              </button>

              {/* Anterior */}
              <button
                className="vi-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                title="Página anterior"
              >
                ‹
              </button>

              {/* Números de página */}
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  className={`vi-page-btn ${currentPage === page ? "vi-page-btn--active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}

              {/* Siguiente */}
              <button
                className="vi-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Página siguiente"
              >
                ›
              </button>

              {/* Última página */}
              <button
                className="vi-page-btn"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Última página"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// ─── SUB-COMPONENTS ────────────────────────────────────────────────────────

function ImagenCell({ ruta, token }) {
  const [open, setOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(false);

  const nombre = ruta?.split(/[/\\]/).pop() || ruta;

  useEffect(() => {
    if (!ruta) return;

    let activo = true;

    const cargarImagen = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/imagen?ruta=${encodeURIComponent(ruta)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
          }
        );

        if (!response.ok) throw new Error();

        const blob = await response.blob();
        if (activo) setImgSrc(URL.createObjectURL(blob));
      } catch {
        if (activo) setImgError(true);
      }
    };

    cargarImagen();
    return () => { activo = false; };
  }, [ruta, token]);

  // Sin ruta registrada, o la descarga falló: no se muestra nada (ni "—" ni ⚠).
  if (!ruta || imgError) return null;

  return (
    <>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={nombre}
          className="vi-miniatura"
          onClick={() => setOpen(true)}
        />
      ) : (
        <Spinner dark />
      )}

      {open && imgSrc && (
        <div className="vi-overlay" onClick={() => setOpen(false)}>
          <div className="vi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vi-modal-header">
              <span className="vi-modal-nombre">{nombre}</span>
              <button className="vi-modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>

            <img src={imgSrc} alt={nombre} className="vi-modal-img" />

            <p className="vi-modal-ruta">{ruta}</p>
          </div>
        </div>
      )}
    </>
  );
}

function ImagenBadge({ count }) {
  const badgeClass =
    count === 4 ? "vi-badge vi-badge--complete" :
    count === 0 ? "vi-badge vi-badge--empty"    :
                  "vi-badge vi-badge--partial";

  return <span className={badgeClass}>{count}/4</span>;
}

function Stat({ label, value, color }) {
  return (
    <div className="vi-stat-card" style={{ borderTopColor: color }}>
      <span className="vi-stat-val" style={{ color }}>{value}</span>
      <span className="vi-stat-lbl">{label}</span>
    </div>
  );
}

function Spinner({ dark }) {
  return (
    <span className={`vi-spinner ${dark ? "vi-spinner--dark" : "vi-spinner--light"}`} />
  );
}

// ─── HELPERS ───────────────────────────────────────────────────────────────

function formatFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      day:    "2-digit",
      month:  "short",
      year:   "numeric",
      hour:   "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
